/**
 * The table's lounge chair (2026-10-07, from the user's reference render): a barrel shell in
 * warm taupe fabric whose back sweeps down into the arms, a cream piping along its rim and
 * down its front edges, a band of dark leather across the top of the back, a deep seat
 * cushion, and a dark swivel neck on a bronze cone with a ring of warm light at its foot.
 *
 * Built in code at about 2,900 triangles: the fabric, piping, leather and cushion are one
 * vertex-coloured geometry, the neck and cone another, the light ring a third (it joins the
 * room's lines of light). The chair faces +z, its back toward -z, standing on y = 0.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

export const CHAIR_COLOURS = {
  fabric: '#b9aa9f',
  fabricInner: '#c2b3a8',
  cushion: '#c6b8ad',
  piping: '#e6dbcf',
  leather: '#4f4844',
  neck: '#2f2b28',
  bronze: '#7d6650',
};

/** The shell wraps this far round either side of the back (the front opening is the rest). */
const WRAP = (135 * Math.PI) / 180;
/** Where the shell starts above the floor, under the cushion. */
const SHELL_BOTTOM = 0.33;
const THICKNESS = 0.06;

const smooth = (a: number, b: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

/** Height of the rim at `u` (0 at the back, 1 at the front edge). */
function rimHeight(u: number): number {
  return 0.64 + 0.3 * (1 - smooth(0.1, 0.7, u)) - 0.1 * smooth(0.8, 1, u);
}

/**
 * The shell's outer radius at height share `t` (0 the bottom, 1 the rim) and `u` round the
 * wall: a rounded bowl, its bulge easing off toward the front edges so they fall cleanly.
 */
function shellRadius(t: number, u = 0): number {
  const bulge = 0.13 * Math.sin((Math.min(1, t / 0.55) * Math.PI) / 2) - 0.02 * smooth(0.55, 1, t);
  return 0.33 + bulge * (1 - 0.35 * smooth(0.7, 1, u));
}

/** A point on the shell: `psi` round from the back, `t` up the wall, `inset` inward. */
function shellPoint(psi: number, t: number, inset: number): THREE.Vector3 {
  const u = Math.abs(psi) / WRAP;
  const y = SHELL_BOTTOM + t * (rimHeight(u) - SHELL_BOTTOM);
  const r = shellRadius(t, u) - inset;
  return new THREE.Vector3(Math.sin(psi) * r, y, -Math.cos(psi) * r);
}

/** Position, normal and a flat colour only, so every part merges with every other. */
function tint(g: THREE.BufferGeometry, hex: string): THREE.BufferGeometry {
  for (const name of Object.keys(g.attributes))
    if (name !== 'position' && name !== 'normal') g.deleteAttribute(name);
  if (!g.attributes.normal) g.computeVertexNormals();
  const c = new THREE.Color(hex);
  const n = g.attributes.position!.count;
  const colours = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) colours.set([c.r, c.g, c.b], i * 3);
  g.setAttribute('color', new THREE.BufferAttribute(colours, 3));
  return g;
}

/** A grid surface over (psi, t), facing outward or inward. */
function surface(
  psi0: number,
  psi1: number,
  t0: number | ((psi: number) => number),
  t1: number | ((psi: number) => number),
  inset: number,
  cols: number,
  rows: number,
  inward: boolean,
): THREE.BufferGeometry {
  const pos: number[] = [];
  const index: number[] = [];
  const at = (f: number | ((psi: number) => number), psi: number) =>
    typeof f === 'number' ? f : f(psi);
  for (let j = 0; j <= rows; j++)
    for (let i = 0; i <= cols; i++) {
      const psi = psi0 + ((psi1 - psi0) * i) / cols;
      const a = at(t0, psi);
      const p = shellPoint(psi, a + ((at(t1, psi) - a) * j) / rows, inset);
      pos.push(p.x, p.y, p.z);
    }
  for (let j = 0; j < rows; j++)
    for (let i = 0; i < cols; i++) {
      const a = j * (cols + 1) + i;
      const b = a + 1;
      const c = a + cols + 1;
      const d = c + 1;
      if (inward) index.push(a, b, c, b, d, c);
      else index.push(a, c, b, b, c, d);
    }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(index);
  g.computeVertexNormals();
  return g.toNonIndexed();
}

/** The piping: down the left front edge, along the rim, down the right front edge. */
function pipingCurve(): THREE.CatmullRomCurve3 {
  const pts: THREE.Vector3[] = [];
  const mid = THICKNESS / 2;
  for (let k = 0; k <= 4; k++) pts.push(shellPoint(-WRAP, 0.12 + (0.88 * k) / 4, mid));
  for (let k = 1; k < 24; k++) pts.push(shellPoint(-WRAP + (2 * WRAP * k) / 24, 1, mid));
  for (let k = 4; k >= 0; k--) pts.push(shellPoint(WRAP, 0.12 + (0.88 * k) / 4, mid));
  return new THREE.CatmullRomCurve3(pts);
}

function merged(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const g = mergeGeometries(parts);
  if (!g) throw new Error('chair: parts do not merge');
  return g;
}

export interface ChairGeometry {
  /** Shell, leather, piping and cushion, vertex-coloured (a matte material). */
  body: THREE.BufferGeometry;
  /** Neck and cone, vertex-coloured (a glossy metal material). */
  base: THREE.BufferGeometry;
  /** The ring of light at the cone's foot (white; the room's light material colours it). */
  light: THREE.BufferGeometry;
}

export function buildChairGeometry(): ChairGeometry {
  const cols = 32;
  const outer = tint(surface(-WRAP, WRAP, 0, 1, 0, cols, 10, false), CHAIR_COLOURS.fabric);
  // The inner wall, with the leather band across the top of the back left out of it.
  // The leather band: deepest at the middle of the back, narrowing toward the arms.
  const band = { psi: 0.7, t1: 0.95 };
  const bandLow = (psi: number) => 0.68 + 0.2 * (psi / band.psi) ** 2;
  const innerParts = [
    surface(-WRAP, WRAP, 0.18, 0.68, THICKNESS, cols, 6, true),
    surface(-WRAP, -band.psi, 0.68, 1, THICKNESS, 10, 3, true),
    surface(band.psi, WRAP, 0.68, 1, THICKNESS, 10, 3, true),
    surface(-band.psi, band.psi, 0.68, bandLow, THICKNESS, 12, 2, true),
    surface(-band.psi, band.psi, band.t1, 1, THICKNESS, 12, 1, true),
  ].map((g) => tint(g, CHAIR_COLOURS.fabricInner));
  const leather = tint(
    surface(-band.psi, band.psi, bandLow, band.t1, THICKNESS + 0.004, 14, 3, true),
    CHAIR_COLOURS.leather,
  );
  const backLow = (psi: number) => 0.76 + 0.2 * (psi / (band.psi * 1.1)) ** 2;
  const leatherBack = tint(
    surface(-band.psi * 1.1, band.psi * 1.1, backLow, 0.985, -0.004, 16, 3, false),
    CHAIR_COLOURS.leather,
  );
  // Front edges: close the wall between the outer and inner surfaces.
  const edges: THREE.BufferGeometry[] = [];
  for (const psi of [-WRAP, WRAP]) {
    const pos: number[] = [];
    const steps = 8;
    for (let k = 0; k < steps; k++) {
      const t0 = (k / steps) * 1;
      const t1 = ((k + 1) / steps) * 1;
      const a = shellPoint(psi, t0, 0);
      const b = shellPoint(psi, t1, 0);
      const c = shellPoint(psi, t0, THICKNESS);
      const d = shellPoint(psi, t1, THICKNESS);
      pos.push(...a.toArray(), ...b.toArray(), ...c.toArray());
      pos.push(...b.toArray(), ...d.toArray(), ...c.toArray());
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.computeVertexNormals();
    edges.push(tint(g, CHAIR_COLOURS.fabric));
  }
  // The underside: a disc closing the shell's bottom.
  const bottom = tint(
    new THREE.CircleGeometry(shellRadius(0), 28)
      .rotateX(Math.PI / 2)
      .translate(0, SHELL_BOTTOM, 0)
      .toNonIndexed(),
    CHAIR_COLOURS.fabric,
  );
  const piping = tint(
    new THREE.TubeGeometry(pipingCurve(), 96, 0.034, 8).toNonIndexed(),
    CHAIR_COLOURS.piping,
  );
  // The seat cushion: a deep rounded pad, a little forward of the shell's centre.
  const cushionProfile: [number, number][] = [
    [0, 0.39],
    [0.29, 0.39],
    [0.33, 0.42],
    [0.34, 0.47],
    [0.32, 0.515],
    [0.22, 0.535],
    [0, 0.54],
  ];
  const cushion = tint(
    new THREE.LatheGeometry(
      cushionProfile.map(([r, y]) => new THREE.Vector2(r, y)),
      28,
    )
      .scale(1, 1, 1.05)
      .translate(0, 0, 0.05)
      .toNonIndexed(),
    CHAIR_COLOURS.cushion,
  );
  const body = merged([
    outer,
    ...innerParts,
    leather,
    leatherBack,
    ...edges,
    bottom,
    piping,
    cushion,
  ]);

  const cone: [number, number][] = [
    [0, 0.045],
    [0.235, 0.045],
    [0.232, 0.065],
    [0.15, 0.25],
    [0, 0.25],
  ];
  const base = merged([
    tint(
      new THREE.LatheGeometry(
        cone.map(([r, y]) => new THREE.Vector2(r, y)),
        28,
      ).toNonIndexed(),
      CHAIR_COLOURS.bronze,
    ),
    tint(
      new THREE.CylinderGeometry(0.11, 0.12, SHELL_BOTTOM - 0.25 + 0.02, 24, 1)
        .translate(0, (0.25 + SHELL_BOTTOM) / 2, 0)
        .toNonIndexed(),
      CHAIR_COLOURS.neck,
    ),
    // A dark foot under the light ring.
    tint(
      new THREE.CylinderGeometry(0.23, 0.23, 0.012, 28).translate(0, 0.006, 0).toNonIndexed(),
      CHAIR_COLOURS.neck,
    ),
  ]);
  const light = new THREE.CylinderGeometry(0.238, 0.238, 0.03, 32, 1, true).translate(0, 0.028, 0);
  return { body, base, light };
}
